import { Component, OnInit, Input, ViewChild, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { Archive } from '@models/Archive';
import { Subscription } from 'rxjs';
import { LoaderService } from '@services/support/loader.service';
import { ArchiveTranscriptionView, SessionService } from '@services/remote/session.service';
import { AuthService } from '@services/auth.service';
import { SnackbarService } from '@services/support/snackbar.service';
import { ModalService } from '@services/remote/modal.service';

@Component({
    selector: 'app-archives',
    templateUrl: './archives.component.html',
    styleUrls: ['./archives.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ArchivesComponent {
  @ViewChild("deleteArchiveTemplate", { static: true }) private deleteArchiveTemplate: TemplateRef<any>;
  @ViewChild("downloadArchiveTemplate", { static: true }) private downloadArchiveTemplate: TemplateRef<any>;
  @ViewChild("watchArchiveTemplate", { static: true }) private watchArchiveTemplate: TemplateRef<any>;
  @ViewChild("transcriptTemplate", { static: true }) private transcriptTemplate: TemplateRef<any>;

  @Input() room_id: string;
  @Input() session_id: string;

  downloadLinks: { [key: string]: { loading: boolean, url?: string } } = {};

  archives: Archive[] = null;
  archivesSub: Subscription = null;

  selectedArchive: Archive = null;

  firstLoaded: boolean = false;
  showArchives: boolean = false;

  pendingExists: boolean = false;
  refreshing: boolean = false;

  dwUrl = "";

  accountDataSub: Subscription = null;
  timezone: string = "UTC";

  webdavEnabled: boolean = false;
  transcriptionViews: { [key: string]: ArchiveTranscriptionView } = {};
  transcriptionLoading: { [key: string]: boolean } = {};
  transcriptionPollingTimers: { [key: string]: any } = {};
  transcriptionPollingStartedAt: { [key: string]: number } = {};
  selectedTranscript: ArchiveTranscriptionView = null;

  constructor(
    private authService: AuthService,
    private sessionService: SessionService,
    private loaderService: LoaderService,
    private snackBarService: SnackbarService,
    private modalService: ModalService
  ) { }

  ngOnInit() {
    this.authService.getAccountData().then(account => {
      this.webdavEnabled = account.data.accountData.add_ons.webdav;
    })
    this.archivesSub = this.sessionService.getSessionArchives(this.room_id, this.session_id)
    .subscribe(archiveList => {
      this.archives = archiveList.sort((a, b) => a.create_time > b.create_time ? -1 : 1);
      this.syncTranscriptionState();

      if (this.archives.length > 0 && this.archives.filter(a => a.storage_status === 'pending').length > 0) {
        this.pendingExists = true;
      } else {
        this.pendingExists = false;
      }

      if (!this.firstLoaded) {
        this.firstLoaded = true;

        if (this.pendingExists) {
          this.sessionService.checkSessionArchives(this.room_id, this.session_id).finally(() => {
            this.showArchives = true;
          });
        } else {
          this.showArchives = true;
        }
      }
    });
  }

  refreshArchives() {
    this.refreshing = true;
    this.sessionService.checkSessionArchives(this.room_id, this.session_id).finally(() => {
      this.refreshing = false;
    });
  }

  ngOnDestroy() {
    if (this.accountDataSub) { this.accountDataSub.unsubscribe() }
    if (this.archivesSub) { this.archivesSub.unsubscribe() }
    Object.keys(this.transcriptionPollingTimers).forEach(archiveId => this.stopTranscriptionPolling(archiveId));
  }

  getArchiveStatusKey(archive: Archive): string {
    switch (archive.storage_status) {
      case "uploaded":
        return "MAIN.ARCHIVES.ARCHIVE_LIST.AVAILABLE";
      case "disconnected":
        return "MAIN.ARCHIVES.ARCHIVE_LIST.DISCONNECTED";
      case "expired":
      case "deleted":
        return "MAIN.ARCHIVES.ARCHIVE_LIST.DELETED";
      case "pending":
        return "MAIN.ARCHIVES.ARCHIVE_LIST.WAITING";
      case "failed":
        return "MAIN.ARCHIVES.ARCHIVE_LIST.FAILED";
      default:
        return "MAIN.ARCHIVES.ARCHIVE_LIST.FAILED";
    }
  }

  getBadgeClass(archive: Archive) {
    switch (archive.storage_status) {
      case "uploaded":
        return {"badge-success": true};    
      case "pending":
        return {"badge-warning": true};
      case "disconnected":
        return {"badge-secondary": true};
      case "expired":
      case "deleted":
      case "failed":
      default:
        return {"badge-danger": true};
    }
  }

  async onDownloadArchive(archive: Archive) {
    if (!(this.downloadLinks[archive.id] && this.downloadLinks[archive.id].url)) {
      this.downloadLinks[archive.id] = { loading: true };
      await this.sessionService.downloadArchive(this.room_id, this.session_id, archive)
      .then(url => this.downloadLinks[archive.id].url = url )
      .catch(error => console.log(error))
      .finally(() => { this.downloadLinks[archive.id].loading = false })
    }
  }

  async onWatchArchive(archive: Archive) {    
    this.selectedArchive = archive;
    const modalId = this.modalService.show({
      template: this.watchArchiveTemplate,
      context: {
        dataModel: "",
        callbacks: {
          close: () => {
            this.modalService.hide(modalId);
            this.dwUrl = ""
          }
        }
      }
    });
    
    if (!(this.downloadLinks[archive.id] && this.downloadLinks[archive.id].url)) {
      this.downloadLinks[archive.id] = { loading: true };
      await this.sessionService.downloadArchive(this.room_id, this.session_id, archive)
      .then(url => this.downloadLinks[archive.id].url = url )
      .catch(error => console.log(error))
      .finally(() => { this.downloadLinks[archive.id].loading = false })
    }
    this.dwUrl = this.downloadLinks[archive.id].url
  }

  getArchiveExtension(archive: Archive): 'mp4' | 'webm' | 'zip' | null {
    if (archive?.outputMode === 'custom') {
      return 'webm';
    }
    if (archive?.outputMode === 'individual') {
      return 'zip';
    }
    if (archive?.storage_status === 'uploaded') {
      return 'mp4';
    }
    return null;
  }

  canSeeTranscriptionAction(): boolean {
    const currentUser = this.authService.currentUser;
    return currentUser?.role === 'admin' || currentUser?.role === 'coadmin' || currentUser?.coadmin === true;
  }

  canTranscribeArchive(archive: Archive): boolean {
    return this.canSeeTranscriptionAction() && archive?.storage_status === 'uploaded' && this.getArchiveExtension(archive) === 'mp4';
  }

  getTranscriptionButtonLabel(archive: Archive): string {
    const state = this.transcriptionViews[archive.id];
    if (this.transcriptionLoading[archive.id] || state?.status === 'running') {
      return 'MAIN.ARCHIVES.ARCHIVE_LIST.TRANSCRIBING';
    }
    if (state?.status === 'done') {
      return 'MAIN.ARCHIVES.ARCHIVE_LIST.VIEW_TRANSCRIPT';
    }
    if (state?.status === 'failed') {
      return 'MAIN.ARCHIVES.ARCHIVE_LIST.RETRY_TRANSCRIPTION';
    }
    return 'MAIN.ARCHIVES.ARCHIVE_LIST.TRANSCRIBE';
  }

  getTranscriptionButtonTitle(archive: Archive): string {
    const state = this.transcriptionViews[archive.id];
    if (this.transcriptionLoading[archive.id] || state?.status === 'running') {
      return 'Transcribing';
    }
    if (state?.status === 'done') {
      return 'Open transcript';
    }
    if (state?.status === 'failed' && state?.error) {
      return state.error;
    }
    return 'Transcribe';
  }

  isTranscriptionBusy(archive: Archive): boolean {
    return this.transcriptionLoading[archive.id] || this.transcriptionViews[archive.id]?.status === 'running';
  }

  async onTranscriptionAction(archive: Archive) {
    const currentView = this.transcriptionViews[archive.id];
    if (currentView?.status === 'done') {
      this.openTranscriptModal(archive, currentView);
      return;
    }

    if (this.isTranscriptionBusy(archive)) {
      return;
    }

    this.transcriptionLoading[archive.id] = true;
    try {
      const view = await this.sessionService.transcribeArchive(this.room_id, this.session_id, archive.id);
      this.transcriptionViews[archive.id] = view;

      if (view?.status === 'done') {
        this.openTranscriptModal(archive, view);
      } else if (view?.status === 'running') {
        this.startTranscriptionPolling(archive.id, view.createTime);
      } else if (view?.status === 'failed') {
        this.snackBarService.error(view?.error || 'Transcription failed');
      }
    } catch (error) {
      this.handleTranscriptionError(error);
    } finally {
      this.transcriptionLoading[archive.id] = false;
    }
  }

  openTranscriptModal(archive: Archive, view?: ArchiveTranscriptionView) {
    this.selectedArchive = archive;
    this.selectedTranscript = view || this.transcriptionViews[archive.id] || null;

    const modalId = this.modalService.show({
      template: this.transcriptTemplate,
      context: {
        dataModel: "",
        callbacks: {
          close: () => {
            this.modalService.hide(modalId);
            this.selectedTranscript = null;
          }
        }
      }
    });
  }

  getTranscriptPlainText(): string {
    const segments = this.selectedTranscript?.transcript?.segments || [];
    return segments.map(segment => {
      const speakerPrefix = segment.speakerLabel ? `${segment.speakerLabel}: ` : '';
      return `[${this.formatSeconds(segment.startSec)} - ${this.formatSeconds(segment.endSec)}] ${speakerPrefix}${segment.text}`;
    }).join('\n\n');
  }

  formatSeconds(totalSeconds?: number): string {
    const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  onTranscriptCopied() {
    this.snackBarService.success('Copied');
  }

  private syncTranscriptionState() {
    const activeArchiveIds = new Set((this.archives || [])
      .filter(archive => this.canTranscribeArchive(archive))
      .map(archive => archive.id));

    Object.keys(this.transcriptionPollingTimers).forEach(archiveId => {
      if (!activeArchiveIds.has(archiveId)) {
        this.stopTranscriptionPolling(archiveId);
      }
    });

    (this.archives || []).forEach(archive => {
      if (!this.canTranscribeArchive(archive)) {
        return;
      }
      if (this.transcriptionViews[archive.id] || this.transcriptionLoading[archive.id]) {
        return;
      }
      this.fetchArchiveTranscription(archive, true);
    });
  }

  private async fetchArchiveTranscription(archive: Archive, showLoader: boolean = false) {
    if (showLoader) {
      this.transcriptionLoading[archive.id] = true;
    }

    try {
      const view = await this.sessionService.getArchiveTranscription(this.room_id, this.session_id, archive.id);
      this.transcriptionViews[archive.id] = view;

      if (view?.status === 'running') {
        this.startTranscriptionPolling(archive.id, view.createTime);
      } else {
        this.stopTranscriptionPolling(archive.id);
      }
    } catch (error) {
      if (error?.status !== 404) {
        this.handleTranscriptionError(error, false);
      }
    } finally {
      if (showLoader) {
        this.transcriptionLoading[archive.id] = false;
      }
    }
  }

  private startTranscriptionPolling(archiveId: string, createTime?: number) {
    if (!this.transcriptionPollingStartedAt[archiveId]) {
      this.transcriptionPollingStartedAt[archiveId] = createTime || Date.now();
    }

    this.stopTranscriptionPolling(archiveId);

    const elapsedMs = Date.now() - this.transcriptionPollingStartedAt[archiveId];
    const nextDelay = elapsedMs < 120000 ? 10000 : 30000;

    this.transcriptionPollingTimers[archiveId] = setTimeout(() => {
      const archive = this.archives?.find(item => item.id === archiveId);
      if (!archive) {
        this.stopTranscriptionPolling(archiveId);
        return;
      }

      this.fetchArchiveTranscription(archive).finally(() => {
        if (this.transcriptionViews[archiveId]?.status === 'running') {
          this.startTranscriptionPolling(archiveId);
        }
      });
    }, nextDelay);
  }

  private stopTranscriptionPolling(archiveId: string) {
    if (this.transcriptionPollingTimers[archiveId]) {
      clearTimeout(this.transcriptionPollingTimers[archiveId]);
      delete this.transcriptionPollingTimers[archiveId];
    }

    if (this.transcriptionViews[archiveId]?.status !== 'running') {
      delete this.transcriptionPollingStartedAt[archiveId];
    }
  }

  private handleTranscriptionError(error: any, showSnackbar: boolean = true) {
    const message = error?.error?.message || error?.message || 'Failed to load transcription';
    if (showSnackbar) {
      this.snackBarService.error(message);
    }
  }

  onDeleteArchive(archive: Archive) {
    // const modalId = this.modalService.show({
    //   template: this.deleteArchiveTemplate,
    //   context: {
    //     dataModel: { shared: archive.storage_shared },
    //     callbacks: {
    //       cancel: () => {
    //         this.modalService.hide(modalId);
    //       },
    //       delete: () => {
    //         this.loaderService.show();
    //         this.sessionService.deleteArchive(this.room_id, this.session_id, archive)
    //         .then(() => this.snackBarService.success("Record deleted."))
    //         .catch(error => this.snackBarService.error('Cannot delete record.'))
    //         .finally(() => {
    //           this.modalService.hide(modalId);
    //           this.loaderService.hide();
    //         });
    //       }
    //     }
    //   }
    // });
  }

}
